<template>
  <div>
    <QuestionContainerBlock
      v-for="(c, idx) in containersSorted"
      :key="`${question._id}-${c.rownum}`"
      :container="c"
      :index="idx"
      :reviewMode="reviewMode"
      :showReview="showReview"
      @containerAnswered="payload => onContainerAnswered(idx, payload)"
    />

    <FlagQuestion :question="question" :childId="studentId" />

    <div v-if="!reviewMode" class="mt-4 flex items-center justify-between">
      <div class="flex items-center gap-4">
        <span class="text-sm text-[var(--mt-text-muted)]">
          Sub-questions answered: {{ answeredCount }} / {{ totalContainers }}
        </span>

        <!-- Learn about this topic link -->
        <router-link
          v-if="learnUrl"
          :to="learnUrl"
          class="text-xs flex items-center gap-1"
          :style="{ color: 'var(--mt-accent-blue)' }"
        >
          <MTIcon name="book-open" :size="14" />
          Learn about this topic
        </router-link>
      </div>

      <button
        class="mt-btn mt-btn-primary"
        :disabled="!allAnswered"
        @click="finalizeQuestion"
      >
        Next →
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import QuestionContainerBlock from "./QuestionContainerBlock.vue";
import FlagQuestion from "./FlagQuestion.vue";
import MTIcon from "@/components/ui/MTIcon.vue";

const props = defineProps({
  question: { type: Object, required: true },
  studentId: { type: String, default: "" },
  lessonId: { type: String, default: "" },
  reviewMode: { type: Boolean, default: false },
  showReview: { type: Boolean, default: false }
});
const submitted = ref(false);
const isCorrect = ref(null);

const emit = defineEmits(["questionCompleted"]);

const results = ref({}); // idx -> container result payload

const containersSorted = computed(() =>
  (props.question.containers || []).slice().sort((a, b) => Number(a.rownum) - Number(b.rownum))
);

const totalContainers = computed(() => containersSorted.value.length);
const answeredCount = computed(() => Object.keys(results.value).length);
const allAnswered = computed(() => answeredCount.value === totalContainers.value);

// Build learn URL if studentId, lessonId, and sectionGroupId are available
const learnUrl = computed(() => {
  if (!props.studentId || !props.lessonId) return null;

  const sectionGroups = props.question.formatsectionGroups || [];
  if (!sectionGroups.length) return null;

  // Use the first section group for the learn link
  return `/student/${props.studentId}/learn/${props.lessonId}/${sectionGroups[0]}`;
});

function onContainerAnswered(index, payload) {
  results.value[index] = payload;
}

// In reviewMode, auto-finalize when all containers answered (no Next button)
watch(allAnswered, (val) => {
  if (val && props.reviewMode) {
    finalizeQuestion();
  }
});

function finalizeQuestion() {
  const containerResults = Object.values(results.value);
  const questionCorrect = containerResults.every(r => r.correct);

  emit("questionCompleted", {
    questionId: props.question._id,
    formatSectionGroups: props.question.formatsectionGroups || [],
    difficulty: props.question.difficulty,
    correct: questionCorrect,
    containers: containerResults
  });

  results.value = {};
}
</script>
